import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, computed, Inject, Input, Optional } from '@angular/core';
import { FacilitiesService } from '@core/data/providers/facilities.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { CategoryElement } from '@core/data/partial/categories.model';
import { map, take } from 'rxjs/operators';
import { PractitionerProvidersService } from '@core/data/providers/practitioner-providers.service';
import { UserMandantFacilitiesService } from '@core/services/user-mandant-facilities.service';
import { select, Store } from '@ngrx/store';
import { showPageHeader, TabComponent, TabGroupComponent } from '@xt/ui-kit';
import { isUserRoleRepresentativeSelector } from '@core/store/selectors/user-role.selectors';
import { CommonModule } from '@angular/common';
import { PageHeaderModule } from '@shared/components/molecules/page-header/page-header.module';
import { CypressDirectiveModule } from '@shared/directives/cypress/cypress-directive.module';
import { MatExpansionModule } from '@angular/material/expansion';
import { TranslocoModule } from '@jsverse/transloco';
import { FilterProviderForm } from '../filter-provider-form/filter-provider-form.component';
import { TableModule } from '@shared/components/molecules/table/table.module';
import { AddProviderTab } from '@shared/models/filter-provider.model';

@Component({
  selector: 'xt-portal-add-provider',
  templateUrl: './add-provider.component.html',
  styleUrls: ['./add-provider.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderModule,
    CypressDirectiveModule,
    MatExpansionModule,
    TabComponent,
    TabGroupComponent,
    TableModule,
    TranslocoModule,
    FilterProviderForm,
  ],
})
export class AddProviderComponent {
  @Input() backlink = {
    title: 'app.profileQuickMenu.privacyCockpit',
    url: '/dashboard/privacy-cockpit',
  };

  public required$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true,
  );

  navigatedFrom = null;
  isUserRoleRepresentative$: Observable<boolean>;
  header;

  constructor(
    private router: Router,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<AddProviderComponent>,
    private facilitiesService: FacilitiesService,
    private practitionerProvidersService: PractitionerProvidersService,
    private userMandantFacilitiesService: UserMandantFacilitiesService,
    private route: ActivatedRoute,
    private store: Store,
  ) {
    this.isUserRoleRepresentative$ = this.store.pipe(
      select(isUserRoleRepresentativeSelector),
    );

    this.store
      .pipe(select(isUserRoleRepresentativeSelector))
      .pipe(
        map((isRep) => {
          return !isRep;
        }),
      )
      .subscribe(this.required$);

    this.isUserRoleRepresentative$.subscribe((isRep) => {
      isRep
        ? (this.header = showPageHeader(
            computed(() => ({
              pageCy: 'privacy-cokpit.representative',
            })),
          ))
        : null;
    });
  }

  // name property in filterFields definitions should be a key of ProviderSearchParams!!!
  public addProviderTabs: AddProviderTab[] = [
    {
      id: 'facility',
      label: 'app.appointments.newAppointment.facility',
      providerService: this.facilitiesService,
      filterFields: {
        standard: [
          {
            name: 'name',
            placeholder: 'app.privacyCockpit.facilityName',
            svgIcon: 'search',
          },
          {
            name: 'zip',
            placeholder: 'app.privacyCockpit.postcode',
            width: 'small',
          },
          { name: 'city', placeholder: 'app.privacyCockpit.location' },
          {
            name: 'mandantId',
            label: 'app.labels.organisation',
            entries$: this.userMandantFacilitiesService.activeFacilities$.pipe(
              map((organizations) =>
                organizations.map(({ name, id }) => ({ name, id })),
              ),
            ),
            required: this.required$.getValue(),
            preselectFirst: true,
            displayed: this.required$.getValue(),
          },
        ],
        advanced: [
          { name: 'street', placeholder: 'app.labels.street' },
          { name: 'house', placeholder: 'app.labels.housenumber' },
          {
            name: 'facilityType',
            label: 'app.labels.facilityType',
            entries$:
              this.facilitiesService.getCategoryOptionsList$('facilityType'),
          },
          {
            name: 'specialty',
            label: 'app.labels.subjectAreaFromFacility',
            entries$:
              this.facilitiesService.getCategoryOptionsList$('specialty'),
          },
        ],
      },
      table: {
        allColumns: ['name', 'specialty', 'address'],
        tabletColumns: ['name', 'specialty'],
        mobileColumns: ['name', 'specialty'],
      },
    },
    //commented out because of CUR-2524
    // {
    //   id: 'station',
    //   label: 'Station',
    //   providerService: this.stationsService,
    //   filterFields: {
    //     standard: [
    //       {
    //         name: 'parentOrganizationId',
    //         label: 'app.privacyCockpit.selectFacility',
    //         entries$: this.getDropdownFacilitiesEntries$(),
    //       },
    //       { name: 'zip',  placeholder: 'app.privacyCockpit.postcode', width: 'small',     },
    //       { name: 'city', placeholder: 'app.privacyCockpit.location',                     },
    //     ],
    //     advanced: [
    //       { name: 'street', placeholder: 'app.labels.street',       },
    //       { name: 'house',  placeholder: 'app.labels.housenumber',  },
    //       {
    //         name: 'facilityType',
    //         label: 'app.labels.facilityType',
    //         entries$: this.stationsService.getCategoryOptionsList$('facilityType'),
    //       },
    //       {
    //         name: 'specialty',
    //         label: 'app.labels.subjectAreaFromFacility',
    //         entries$: this.stationsService.getCategoryOptionsList$('specialty'),
    //       },
    //     ],
    //   },
    //   table: {
    //     allColumns: ['name', 'specialty', 'address'],
    //     tabletColumns: ['name', 'specialty'],
    //     mobileColumns: ['name', 'specialty'],
    //   }
    // },
    {
      id: 'practitioner',
      label: 'app.appointments.newAppointment.practitioner',
      providerService: this.practitionerProvidersService,
      filterFields: {
        standard: [
          {
            name: 'lastName',
            placeholder: 'app.labels.lastName',
            svgIcon: 'search',
          },
          { name: 'firstName', placeholder: 'app.labels.firstName' },
          {
            name: 'zip',
            placeholder: 'app.privacyCockpit.postcode',
            width: 'small',
          },
          { name: 'city', placeholder: 'app.privacyCockpit.location' },
          {
            name: 'mandantId',
            label: 'app.labels.organisation',
            entries$: this.userMandantFacilitiesService.activeFacilities$.pipe(
              map((organizations) =>
                organizations.map(({ name, identifier }) => ({
                  name,
                  id: identifier,
                })),
              ),
            ),
            displayed: this.required$.getValue(),
            required: this.required$.getValue(),
            preselectFirst: true,
          },
        ],
        advanced: [
          { name: 'street', placeholder: 'app.labels.street' },
          { name: 'house', placeholder: 'app.labels.housenumber' },
          {
            name: 'facilityType',
            label: 'app.privacyCockpit.typeOfHandler',
            entries$:
              this.practitionerProvidersService.getCategoryOptionsList$(
                'facilityType',
              ),
          },
          {
            name: 'specialty',
            label: 'app.privacyCockpit.medicalSpeciality',
            entries$:
              this.practitionerProvidersService.getCategoryOptionsList$(
                'specialty',
              ),
          },
        ],
      },
      table: {
        allColumns: ['name', 'specialty', 'organizationName'],
        tabletColumns: ['name', 'specialty'],
        mobileColumns: ['name', 'specialty'],
      },
    },
  ];

  ngOnInit() {
    if (this.data) {
      this.backlink = {
        title: 'Zurück',
        url: null,
      };
    }
    this.addProviderTabs.forEach((tab) => {
      tab.providerService.clearData();
    });
  }

  public getDropdownFacilitiesEntries$(): Observable<CategoryElement[]> {
    return this.facilitiesService.searchItems$().pipe(
      take(1),
      map((organizations) =>
        organizations.map(({ id, name }) => ({ id, name })),
      ),
    );
  }

  openDetailPage(item) {
    this.navigatedFrom = this.route.snapshot?.url[0]?.parameters?.from || null;
    if (this.data) {
      this.dialogRef.close(item);
    } else {
      this.isUserRoleRepresentative$.subscribe((isRep) => {
        if (isRep) {
          this.router.navigate(
            [
              '../../privacy-cockpit/' + item.identifier,
              { from: this.navigatedFrom, practitioner: item.name },
            ],
            { relativeTo: this.route, queryParams: { title: item.name } },
          );
        } else {
          this.router.navigate([
            'dashboard/privacy-cockpit/' + item.identifier,
            { from: this.navigatedFrom, practitioner: item.name },
          ]);
        }
      });
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
